/**
 * @module components/AmbientMode/SceneEngine
 * @description Motor de rotación de escenas del modo ambient.
 *
 * Gestiona el ciclo de vida de las 4 escenas (compliance, network, activity, institutional)
 * según la configuración `SCENE_ROTATION` de `types/ambient.ts`. Cada escena tiene
 * su propia duración en ms. Al finalizar el tiempo, aplica fade-out y transiciona
 * a la siguiente escena en orden circular.
 *
 * Usa `memo()` para evitar re-renders del motor cuando el overlay padre se actualiza.
 * La transición entre escenas usa CSS opacity para suavizar el cambio visual.
 *
 * @see SCENE_ROTATION en types/ambient.ts para el orden y duración de escenas
 * @see ComplianceScene, NetworkScene, ActivityScene, InstitutionalScene para el contenido
 */
import React, { memo, useEffect, useRef, useState } from 'react';
import { SCENE_ROTATION } from '../../types/ambient';
import type { SceneId } from '../../types/ambient';
import { ComplianceScene }    from './scenes/ComplianceScene';
import { NetworkScene }       from './scenes/NetworkScene';
import { ActivityScene }      from './scenes/ActivityScene';
import { InstitutionalScene } from './scenes/InstitutionalScene';

const SCENE_COMPONENTS: Record<SceneId, React.ComponentType> = {
  compliance:    ComplianceScene,
  network:       NetworkScene,
  activity:      ActivityScene,
  institutional: InstitutionalScene,
};

const CROSSFADE_MS = 600;

interface SceneEngineProps {
  /** Ref al elemento raíz del overlay — para aplicar/quitar clase ambient-paused */
  overlayRef: React.RefObject<HTMLDivElement>;
}

export const SceneEngine = memo(function SceneEngine({ overlayRef }: SceneEngineProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [transitioning, setTransitioning] = useState(false);
  const indexRef = useRef(0);

  // Avanzar a la siguiente escena
  const advance = () => {
    setTransitioning(true);
    setTimeout(() => {
      const next = (indexRef.current + 1) % SCENE_ROTATION.length;
      indexRef.current = next;
      setCurrentIndex(next);
      setTransitioning(false);
    }, CROSSFADE_MS);
  };

  // Timer de duración de cada escena
  useEffect(() => {
    const duration = SCENE_ROTATION[currentIndex].durationMs;
    const timer = setTimeout(advance, duration);
    return () => clearTimeout(timer);
  }, [currentIndex]); // se re-programa en cada cambio de escena

  // Page Visibility: pausar/reanudar animaciones CSS
  useEffect(() => {
    const root = overlayRef.current;
    if (!root) return;

    const handleVisibility = () => {
      if (document.hidden) {
        root.classList.add('ambient-paused');
      } else {
        root.classList.remove('ambient-paused');
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [overlayRef]);

  const SceneComponent = SCENE_COMPONENTS[SCENE_ROTATION[currentIndex].id];

  return (
    <div className="absolute inset-0">
      {/* Crossfade wrapper */}
      <div
        className={`absolute inset-0 transition-opacity duration-[600ms] ${
          transitioning ? 'opacity-0' : 'opacity-100'
        }`}
      >
        <SceneComponent />
      </div>

      {/* Scene indicator dots */}
      <div className="absolute bottom-3 right-6 flex gap-1.5">
        {SCENE_ROTATION.map((scene, i) => (
          <div
            key={scene.id}
            className={`rounded-full transition-all duration-500 ${
              i === currentIndex
                ? 'w-4 h-1.5 bg-blue-400'
                : 'w-1.5 h-1.5 bg-gray-700'
            }`}
          />
        ))}
      </div>
    </div>
  );
});
